/**
 * Búsqueda por palabras y resaltado de la coincidencia.
 *
 * Es la mitad lógica de `.dxc-mark`: la primitiva pinta el tramo que coincide y
 * esto decide cuál es. Va aquí y no en una vista porque el producto ya lo
 * necesitaba en dos sitios con dos reglas distintas —`dxc-select` resaltaba UNA
 * subcadena y el listado de ubicaciones buscaba por prefijo del nombre—, y dos
 * respuestas distintas a «¿esto coincide con lo que escribí?» en la misma
 * sesión es exactamente la deriva que el catálogo existe para evitar.
 *
 * Sin Angular dentro, a propósito: es lo que permite probarlo como una función.
 */

/** Un tramo de texto y si es coincidencia. La unidad que se pinta. */
export interface DxcTextSegment {
    text: string;
    match: boolean;
}

/**
 * Sin acentos, sin mayúsculas. **No colapsa espacios**: las posiciones tienen
 * que seguir siendo comparables con las del texto original, y un `replace` de
 * espacios las desplaza.
 */
export function dxcNormalizeText (value: unknown): string {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}

/**
 * Lo tecleado, partido en palabras y normalizado.
 *
 * Partir es la diferencia entre «Merced» encontrando «Clinica la Merced» y no
 * encontrándola: con la cadena entera, el orden en que el usuario recuerda el
 * nombre tiene que ser el orden en que alguien lo escribió al darlo de alta.
 */
export function dxcQueryTokens (query: string): string[] {
    const tokens = dxcNormalizeText(query).split(/\s+/).filter(Boolean);
    return [...new Set(tokens)];
}

/**
 * ¿Aparecen **todas** las palabras en alguno de los campos?
 *
 * `AND` y no `OR`: con `OR`, teclear una palabra más ampliaría el resultado en
 * vez de acotarlo, que es lo contrario de lo que hace quien sigue escribiendo
 * porque le salen demasiadas filas. Y cada palabra se busca contra los campos
 * **unidos**, así que «merced monterrey» cruza el nombre con el lugar.
 */
export function dxcMatchesTokens (tokens: string[], ...fields: unknown[]): boolean {
    if (!tokens.length) return true;

    const haystack = fields.map(dxcNormalizeText).join(' ');

    return tokens.every(token => haystack.includes(token));
}

/**
 * Parte el texto en tramos, marcando los que coinciden con alguna palabra.
 *
 * Devuelve **estructura y no HTML**, que es la diferencia con `highlight()` de
 * `dxc-select`: aquel arma una cadena con `<mark>` dentro, la escapa a mano y la
 * pasa por `bypassSecurityTrustHtml`, o sea que cada consumidor se trae su
 * propio escapado y una firma que dice «confía en esto». Con segmentos, quien
 * pinta usa `@for` y la interpolación de Angular escapa sola.
 *
 * El mapa de posiciones existe porque normalizar **cambia la longitud**: «é» en
 * NFD son dos unidades y el acento se cae, así que un índice sobre el texto
 * normalizado no sirve para cortar el original. Se normaliza carácter a
 * carácter guardando de dónde salió cada uno.
 */
export function dxcHighlightSegments (value: unknown, tokens: string[]): DxcTextSegment[] {
    const text = String(value ?? '');

    if (!text) return [];
    if (!tokens.length) return [{ text, match: false }];

    let normalized = '';
    const origin: number[] = [];          // posición normalizada → posición original
    const produced: number[] = [];        // cuántas unidades produjo cada original

    for (let index = 0; index < text.length; index++) {
        const piece = dxcNormalizeText(text[index]);
        produced.push(piece.length);
        for (let unit = 0; unit < piece.length; unit++) {
            normalized += piece[unit];
            origin.push(index);
        }
    }

    const hit = new Array<boolean>(text.length).fill(false);

    for (const token of tokens) {
        let at = normalized.indexOf(token);
        while (at >= 0) {
            for (let unit = at; unit < at + token.length; unit++) hit[origin[unit]] = true;
            at = normalized.indexOf(token, at + token.length);
        }
    }

    // Un carácter que no produce nada al normalizar —un acento suelto, si el
    // texto viene ya descompuesto— no lo marca nadie, y sin esto partiría en dos
    // el tramo resaltado justo por la letra acentuada que se buscaba.
    for (let index = 1; index < text.length; index++) {
        if (!produced[index]) hit[index] = hit[index - 1];
    }

    const segments: DxcTextSegment[] = [];

    for (let index = 0; index < text.length; index++) {
        const last = segments[segments.length - 1];
        if (last && last.match === hit[index]) last.text += text[index];
        else segments.push({ text: text[index], match: hit[index] });
    }

    return segments;
}
